import { EM_DASH } from '../lib/typography';
import { Component } from 'preact';
import { isWeb } from '../utils/platform';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  GOLD,
  TEXT_PRIMARY,
  TEXT_BODY,
  TEXT_MUTED,
  ACCENT_GOLD,
  TEXTURED_PANEL_STYLE,
  SUB_PANEL_STYLE,
  SECTION_HEADER_STYLE,
  FourCorners,
  OrnamentalDivider,
  CONTENT_BG_DEFAULT,
} from '../lib/medievalTheme';
import { Tooltip } from './ui/tooltip';
import { t } from '../localization/t';

// Fully opaque base color for the stone texture stack. TEXTURED_PANEL_STYLE
// uses PANEL_BG (rgba 0.92) as its base, which is fine for centered modals
// that float over a darkened scrim. As a popover floating over a busy
// screen, we need a fully opaque base so nothing bleeds through.
const OPAQUE_PANEL_BG = '#0e0c08';

// ── Concentric radii ───────────────────────────────────────
// Every nested surface derives its radius from its parent's radius minus
// the gap between them, so the curves stay parallel instead of drifting:
//
//   panel   1.20cqw  ─ padding 0.70cqw ─→  sub-panel 0.50cqw
//   sub     0.50cqw  ─ padding 0.25cqw ─→  row       0.25cqw
//   menu    0.70cqw  ─ padding 0.35cqw ─→  menu item 0.35cqw
const PANEL_RADIUS = 'var(--radius-2xl)';
const PANEL_PADDING = '0.5cqw';
const SUB_RADIUS = 'var(--radius-lg)';
const LIST_PADDING = '0.25cqw';
const ROW_RADIUS = 'var(--radius-sm)';
const MENU_RADIUS = 'var(--radius-xl)';
const MENU_PADDING = '0.375cqw';
const MENU_ITEM_RADIUS = 'var(--radius-md)';
// Free-floating controls aren't flush against a padding box, so they get a
// radius chosen for their own size rather than a derived one.
const CTRL_RADIUS = 'var(--radius-md)';

const OPAQUE_TEXTURED_PANEL_STYLE = {
  ...TEXTURED_PANEL_STYLE,
  background: `${CONTENT_BG_DEFAULT}, ${OPAQUE_PANEL_BG}`,
};
const OPAQUE_SUB_PANEL_STYLE = {
  ...SUB_PANEL_STYLE,
  background: `${CONTENT_BG_DEFAULT}, ${OPAQUE_PANEL_BG}`,
  borderRadius: SUB_RADIUS,
};
import {
  AMBIENCE_TRACKS,
  playAmbience,
  pauseAmbience,
  setAmbienceTrack,
  setAmbienceVolume,
  getAmbienceState,
  subscribeAmbience,
} from '../utils/arena/ambienceManager';
import {
  getCustomMusicState,
  subscribeCustomMusic,
  hydrateCustomMusic,
  playTrack as cmPlay,
  pauseTrack as cmPause,
  stopTrack as cmStop,
  pickFolder as cmPickFolder,
  clearFolder as cmClearFolder,
  renameTrack as cmRename,
  setTrackLoop as cmSetTrackLoop,
  setTrackGain as cmSetTrackGain,
  reorderTracks as cmReorderTracks,
  setLibraryLoop as cmSetLibraryLoop,
  seekTo as cmSeek,
  updateCustomMusicVolume,
} from '../utils/arena/customMusicManager';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { getSoundSettings, saveSoundSettings } from '../utils/arena/soundSettings';
import { updateMusicVolume } from '../utils/arena/musicManager';
import { playUI, UI } from '../utils/arena/uiSounds';

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '--:--';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default class AudioMixerPopover extends Component {
  state = {
    ambience: getAmbienceState(),
    customMusic: getCustomMusicState(),
    soundSettings: getSoundSettings(),
    rowMenuOpenFor: null,
    rowMenuVariant: 'menu',
    rowMenuAnchor: null,
    folderMenuOpen: false,
    folderMenuAnchor: null,
    renamingId: null,
    renameDraft: '',
    isPickingFolder: false,
    hoveredRowId: null,
    draggingId: null,
    dropTargetId: null,
    dropBelow: false,
  };

  popoverRef = null;
  rowRefs = new Map();

  componentDidMount() {
    this.unsubAmbience = subscribeAmbience(() => {
      this.setState({ ambience: getAmbienceState() });
    });
    this.unsubCustomMusic = subscribeCustomMusic(() => {
      this.setState({ customMusic: getCustomMusicState() });
    });
    hydrateCustomMusic().catch(() => {});

    // Click-outside can't be left to the caller's backdrop overlay: in the
    // header the backdrop is a `position: fixed` child of an element with
    // `backdrop-filter`, which makes that element the containing block, so
    // "inset-0" only ever covered the header bar itself. A document-level
    // listener has no such dependency on where the popover is mounted.
    //
    // It acts on `click` (bubble), not `pointerdown`, so a click on the
    // trigger button reaches the trigger's own toggle first and closes the
    // popover exactly once instead of closing and immediately reopening.
    // Attaching on a later task keeps the click that opened the popover from
    // being seen as an outside click.
    //
    // Containment is decided in the capture phase because by the time the
    // event bubbles up to document, a handler in between may already have
    // removed the element that was clicked. Dismissing a track's ⋯ menu
    // detaches its own click-shield, and `contains()` on a detached node is
    // always false, which read as a click outside the whole popover.
    this._attachTimer = setTimeout(() => {
      document.addEventListener('click', this.handleDocumentClickCapture, true);
      document.addEventListener('click', this.handleDocumentClick);
    }, 0);

    // Escape dismissal goes through the central stack. The inline rename
    // input handles its own Escape locally and the app-level Escape handler
    // bails on editable targets, so a rename in progress cancels the rename
    // rather than closing the popover.
    this._escDismissId = pushDismiss(() => this.props.onClose?.());
  }

  componentWillUnmount() {
    this.unsubAmbience?.();
    this.unsubCustomMusic?.();
    clearTimeout(this._attachTimer);
    document.removeEventListener('click', this.handleDocumentClickCapture, true);
    document.removeEventListener('click', this.handleDocumentClick);
    removeDismiss(this._escDismissId);
  }

  handleDocumentClickCapture = (e) => {
    this._clickedInside = this.popoverRef?.contains(e.target) === true;
  };

  handleDocumentClick = () => {
    if (this._clickedInside) return;
    this.props.onClose?.();
  };

  handleMusicSliderChange = (value) => {
    const next = { ...this.state.soundSettings, musicVolume: value };
    saveSoundSettings(next);
    this.setState({ soundSettings: next });
    updateMusicVolume();
    updateCustomMusicVolume();
  };

  handleToggleMusicEnabled = () => {
    playUI(UI.CLICK);
    const next = { ...this.state.soundSettings, musicEnabled: !this.state.soundSettings.musicEnabled };
    saveSoundSettings(next);
    this.setState({ soundSettings: next });
    updateMusicVolume();
    updateCustomMusicVolume();
  };

  handlePickFolder = async () => {
    if (this.state.isPickingFolder) return;
    this.setState({ isPickingFolder: true, folderMenuOpen: false });
    try {
      const folder = await cmPickFolder();
      if (folder) toast.success(t('Watching {folder} for music.', { folder }));
    } catch (err) {
      toast.error(err?.message || t('Could not set the music folder.'));
    } finally {
      this.setState({ isPickingFolder: false });
    }
  };

  handleClearFolder = async () => {
    this.setState({ folderMenuOpen: false });
    try {
      await cmClearFolder();
      toast.success(t('Music folder cleared.'));
    } catch {
      toast.error(t('Could not clear the music folder.'));
    }
  };

  handleFolderIconClick = (e) => {
    const hasFolder = Boolean(this.state.customMusic.library.folderPath);
    if (!hasFolder) {
      this.handlePickFolder();
      return;
    }
    if (this.state.folderMenuOpen) {
      this.setState({ folderMenuOpen: false, folderMenuAnchor: null });
      return;
    }
    if (!this.popoverRef || !e.currentTarget) return;
    const popoverRect = this.popoverRef.getBoundingClientRect();
    const triggerRect = e.currentTarget.getBoundingClientRect();
    this.setState({
      folderMenuOpen: true,
      folderMenuAnchor: {
        top: triggerRect.bottom - popoverRect.top + 4,
        left: triggerRect.left - popoverRect.left,
      },
    });
  };

  closeFolderMenu = () => {
    this.setState({ folderMenuOpen: false, folderMenuAnchor: null });
  };

  startRename = (track) => {
    this.setState({
      renamingId: track.id,
      renameDraft: track.displayName,
      rowMenuOpenFor: null,
    });
  };

  commitRename = async () => {
    const id = this.state.renamingId;
    const value = this.state.renameDraft.trim();
    this.setState({ renamingId: null, renameDraft: '' });
    if (!id || !value) return;
    try {
      await cmRename(id, value);
    } catch {
      toast.error(t('Could not save the new name.'));
    }
  };

  cancelRename = () => {
    this.setState({ renamingId: null, renameDraft: '' });
  };

  handleRowActivate = (track) => {
    if (this.state.renamingId === track.id) return;
    const cm = this.state.customMusic;
    if (cm.currentTrackId === track.id) {
      if (cm.isPlaying) cmPause();
      else cmPlay(track.id);
    } else {
      cmPlay(track.id);
    }
  };

  handleRowKeyDown = (e, track) => {
    if (this.state.renamingId === track.id) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      playUI(UI.CLICK);
      this.handleRowActivate(track);
    }
  };

  handleDragStart = (e, track) => {
    e.dataTransfer.setData('application/vh-music-id', track.id);
    e.dataTransfer.effectAllowed = 'move';
    // The drag handle is a few pixels wide, and the browser's default drag
    // image is a snapshot of the dragged element. So without this the
    // player drags an almost invisible sliver of grip dots. Snapshot the
    // whole row instead, grabbed at the point the pointer went down.
    const row = this.rowRefs.get(track.id);
    if (row && e.dataTransfer.setDragImage) {
      const rect = row.getBoundingClientRect();
      e.dataTransfer.setDragImage(row, e.clientX - rect.left, e.clientY - rect.top);
    }
    this.setState({ draggingId: track.id });
  };

  handleDragEnd = () => {
    this.setState({ draggingId: null, dropTargetId: null, dropBelow: false });
  };

  handleRowDragOver = (e, track) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const below = e.clientY > rect.top + rect.height / 2;
    if (this.state.dropTargetId !== track.id || this.state.dropBelow !== below) {
      this.setState({ dropTargetId: track.id, dropBelow: below });
    }
  };

  handleDropOnRow = async (e, targetTrack) => {
    e.preventDefault();
    const movedId = e.dataTransfer.getData('application/vh-music-id') || this.state.draggingId;
    const rect = e.currentTarget.getBoundingClientRect();
    const below = e.clientY > rect.top + rect.height / 2;
    this.setState({ draggingId: null, dropTargetId: null, dropBelow: false });
    if (!movedId || movedId === targetTrack.id) return;
    const ids = this.state.customMusic.library.tracks.map((t) => t.id);
    const from = ids.indexOf(movedId);
    if (from < 0 || !ids.includes(targetTrack.id)) return;
    ids.splice(from, 1);
    // Resolve the target index after the removal so dragging downwards
    // lands where the indicator was drawn rather than one slot short.
    const to = ids.indexOf(targetTrack.id) + (below ? 1 : 0);
    ids.splice(to, 0, movedId);
    try {
      await cmReorderTracks(ids);
    } catch {
      toast.error(t('Could not reorder library.'));
    }
  };

  openRowMenu = (track, variant, triggerEl) => {
    if (!this.popoverRef || !triggerEl) return;
    const popoverRect = this.popoverRef.getBoundingClientRect();
    const triggerRect = triggerEl.getBoundingClientRect();
    this.setState({
      rowMenuOpenFor: track.id,
      rowMenuVariant: variant,
      rowMenuAnchor: {
        top: triggerRect.bottom - popoverRect.top + 4,
        right: popoverRect.right - triggerRect.right,
      },
    });
  };

  closeRowMenu = () => {
    this.setState({ rowMenuOpenFor: null, rowMenuAnchor: null });
  };

  /**
   * Repeat cycles off → library → track, the idiom every music player
   * uses. It replaces the two separate loop toggles (a library-loop
   * button in the header and a track-loop button in the transport) that
   * previously sat two rows apart and read as the same control twice.
   */
  cycleRepeat = () => {
    playUI(UI.CLICK);
    const cm = this.state.customMusic;
    const current = cm.library.tracks.find((t) => t.id === cm.currentTrackId);
    const mode = repeatMode(cm, current);
    if (mode === 'off') {
      cmSetLibraryLoop(true);
    } else if (mode === 'all') {
      cmSetLibraryLoop(false);
      // With no track loaded there is no "repeat one" to step into, so
      // library-repeat is a plain on/off toggle.
      if (current) cmSetTrackLoop(current.id, true);
    } else {
      if (current) cmSetTrackLoop(current.id, false);
      cmSetLibraryLoop(false);
    }
  };

  render() {
    const {
      positionClassName = 'absolute left-12 top-0',
      positionStyle = null,
      extraAmbienceSection = null,
      anchor = 'top-right',
    } = this.props;
    const cm = this.state.customMusic;
    const amb = this.state.ambience;
    const ss = this.state.soundSettings;
    const tracks = cm.library.tracks;
    const isEmpty = tracks.length === 0;
    const currentTrack = tracks.find((t) => t.id === cm.currentTrackId) || null;

    const ambTracks = AMBIENCE_TRACKS;
    const currentIdx = ambTracks.findIndex((t) => t.id === amb.trackId);
    const safeIdx = currentIdx >= 0 ? currentIdx : 0;
    const currentAmbTrack = ambTracks[safeIdx];
    const cycleAmbience = (delta) => {
      const next = ambTracks[(safeIdx + delta + ambTracks.length) % ambTracks.length];
      setAmbienceTrack(next.id);
    };

    const centered = anchor === 'left-center';
    const reduced = prefersReducedMotion();
    // Origin-aware entry: the panel grows out of whichever edge it is
    // pinned to, so it reads as unfolding from its trigger.
    const offsetY = centered ? '-50%' : 0;
    // Enter is unhurried enough to read as the panel unfolding; exit is
    // roughly half that, because by then the player has already decided.
    const enter = { duration: reduced ? 0.12 : 0.24, ease: [0.23, 1, 0.32, 1] };
    const leave = { duration: reduced ? 0.1 : 0.14, ease: [0.4, 0, 1, 1] };
    const variants = reduced
      ? {
          hidden: { opacity: 0, y: offsetY },
          visible: { opacity: 1, y: offsetY, transition: enter },
          exit: { opacity: 0, y: offsetY, transition: leave },
        }
      : {
          hidden: { opacity: 0, scale: 0.96, x: centered ? -10 : 0, y: centered ? offsetY : -8 },
          visible: { opacity: 1, scale: 1, x: 0, y: offsetY, transition: enter },
          exit: { opacity: 0, scale: 0.97, x: centered ? -6 : 0, y: centered ? offsetY : -6, transition: leave },
        };

    return (
      <motion.div
        ref={(el) => { this.popoverRef = el; }}
        className={positionClassName}
        variants={variants}
        initial="hidden"
        animate="visible"
        exit="exit"
        style={{
          width: '26cqw',
          padding: PANEL_PADDING,
          ...OPAQUE_TEXTURED_PANEL_STYLE,
          borderRadius: PANEL_RADIUS,
          transformOrigin: centered ? 'left center' : 'top right',
          ...(positionStyle || {}),
        }}
      >
        <FourCorners color={`${GOLD} 0.55)`} size="1.7cqw" />

        {/* ── Music section ─────────────────────────────────── */}
        {/* Custom music needs the native folder picker + fs watcher,
            so the whole library block is desktop-only. The built-in
            music VolumeRow below stays on web. */}
        {isWeb() ? null : (<>
        <div className="flex items-center" style={{ marginBottom: '0.5cqw', gap: '0.55cqw', padding: `0 ${LIST_PADDING}` }}>
          <SectionLabel>{t('Music Library')}</SectionLabel>
          <Tooltip
            content={
              this.state.isPickingFolder
                ? t('Opening…')
                : cm.library.folderPath
                  ? t('Music folder')
                  : t('Choose a folder to watch for music')
            }
            side="bottom"
          >
            <GhostIconButton
              active={this.state.folderMenuOpen}
              onClick={this.handleFolderIconClick}
              disabled={this.state.isPickingFolder}
              aria-label={cm.library.folderPath ? t('Music folder options') : t('Choose folder')}
            >
              <FolderIcon />
            </GhostIconButton>
          </Tooltip>
          <span className="tabular-nums ml-auto" style={{ fontSize: '0.6cqw', color: TEXT_MUTED, letterSpacing: '0.05em' }}>
            {tracks.length > 0
              ? (tracks.length === 1 ? t('1 track') : t('{n} tracks', { n: tracks.length }))
              : cm.library.folderPath ? t('No audio files in folder') : t('No folder set')}
          </span>
        </div>

        {isEmpty ? (
          <button
            type="button"
            className="vh-music-empty w-full flex flex-col items-center justify-center text-center"
            style={{
              ...OPAQUE_SUB_PANEL_STYLE,
              minHeight: '13cqw',
              padding: '1.5cqw',
              borderStyle: 'dashed',
              borderColor: `${GOLD} 0.28)`,
              color: TEXT_MUTED,
              opacity: this.state.isPickingFolder ? 0.55 : 1,
              transition: 'border-color 180ms ease, color 180ms ease, opacity 180ms ease',
            }}
            onClick={this.handlePickFolder}
            disabled={this.state.isPickingFolder}
          >
            <span className="vh-music-empty-icon" style={{ lineHeight: 0, transition: 'transform 220ms cubic-bezier(0.23,1,0.32,1)' }}>
              <FolderIcon size="1.4cqw" />
            </span>
            <div style={{ marginTop: '0.4cqw', fontSize: '0.72cqw', color: TEXT_BODY, letterSpacing: '0.02em' }}>
              {cm.library.folderPath
                ? (cm.library.folderMissing ? t('Folder unavailable') : t('No audio files found'))
                : t('Choose your music folder')}
            </div>
            <div style={{ marginTop: '0.15cqw', fontSize: '0.55cqw', color: TEXT_MUTED }}>
              {cm.library.folderPath
                ? t('Add mp3, m4a, aac, wav, ogg, or flac files to this folder')
                : t('Pick a folder to watch · mp3, m4a, aac, wav, ogg, flac')}
            </div>
          </button>
        ) : (
          <div
            className="overflow-y-auto"
            style={{
              ...OPAQUE_SUB_PANEL_STYLE,
              minHeight: '13cqw',
              maxHeight: '26cqw',
              padding: LIST_PADDING,
              borderColor: `${GOLD} 0.3)`,
            }}
            onDragLeave={(e) => {
              // Row-to-row moves fire dragleave on the list too; only drop
              // the indicator once the pointer has left the list entirely.
              if (!e.currentTarget.contains(e.relatedTarget)) {
                this.setState({ dropTargetId: null });
              }
            }}
          >
            {tracks.map((track, idx) => this.renderTrackRow(track, cm, idx))}
          </div>
        )}

        <PlaybackProgress
          currentTimeSec={cm.currentTimeSec}
          durationSec={cm.durationSec}
          isPlaying={cm.isPlaying}
          hasTrack={Boolean(cm.currentTrackId)}
          repeat={repeatMode(cm, currentTrack)}
          onSeek={(s) => cmSeek(s)}
          onTogglePlay={() => {
            if (!cm.currentTrackId) return;
            playUI(UI.CLICK);
            cm.isPlaying ? cmPause() : cmPlay(cm.currentTrackId);
          }}
          onCycleRepeat={this.cycleRepeat}
        />
        </>)}

        <VolumeRow
          value={ss.musicVolume}
          muted={!ss.musicEnabled}
          onToggleMute={this.handleToggleMusicEnabled}
          onChange={this.handleMusicSliderChange}
        />

        <OrnamentalDivider className="mt-2 mb-2" />

        {/* ── Ambience picker + volume ─────────────────────── */}
        <div className="flex items-center" style={{ marginBottom: '0.45cqw', gap: '0.5cqw', padding: `0 ${LIST_PADDING}` }}>
          <SectionLabel>{t('Ambience')}</SectionLabel>
          <span className="tabular-nums ml-auto" style={{ fontSize: '0.6cqw', color: TEXT_MUTED, letterSpacing: '0.05em' }}>
            {amb.isPlaying ? t('Playing') : t('Paused')}
          </span>
        </div>

        <div className="flex items-center" style={{ gap: '0.45cqw', padding: `0 ${LIST_PADDING}` }}>
          <GhostIconButton
            active={amb.isPlaying}
            onClick={() => { playUI(UI.CLICK); amb.isPlaying ? pauseAmbience() : playAmbience(); }}
            size="1.6cqw"
            aria-label={amb.isPlaying ? t('Pause ambience') : t('Play ambience')}
          >
            {amb.isPlaying ? <PauseIcon small /> : <PlayIcon small />}
          </GhostIconButton>
          <GhostIconButton
            size="1.3cqw"
            onClick={() => { playUI(UI.CLICK); cycleAmbience(-1); }}
            aria-label={t('Previous ambience')}
          >
            <ChevronIcon dir="prev" />
          </GhostIconButton>
          <span
            className="flex-1 text-center truncate"
            style={{
              fontSize: '0.72cqw',
              fontWeight: 500,
              color: amb.isPlaying ? TEXT_PRIMARY : TEXT_BODY,
              letterSpacing: '0.02em',
              padding: '0.22cqw 0.3cqw',
              borderRadius: CTRL_RADIUS,
              background: 'rgba(0,0,0,0.3)',
              boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.5)',
              transition: 'color 160ms ease',
            }}
          >
            {t(currentAmbTrack.label)}
          </span>
          <GhostIconButton
            size="1.3cqw"
            onClick={() => { playUI(UI.CLICK); cycleAmbience(1); }}
            aria-label={t('Next ambience')}
          >
            <ChevronIcon dir="next" />
          </GhostIconButton>
        </div>

        <VolumeRow value={amb.volume} onChange={(v) => setAmbienceVolume(v)} />

        {extraAmbienceSection ? (
          <>
            <OrnamentalDivider className="mt-3 mb-2" />
            {extraAmbienceSection}
          </>
        ) : null}

        <AnimatePresence>
          {this.state.rowMenuOpenFor ? this.renderRowMenuOverlay() : null}
        </AnimatePresence>
        <AnimatePresence>
          {this.state.folderMenuOpen ? this.renderFolderMenu() : null}
        </AnimatePresence>
      </motion.div>
    );
  }

  renderTrackRow(track, cm, idx) {
    const isCurrent = cm.currentTrackId === track.id;
    const isPlaying = isCurrent && cm.isPlaying;
    const isRenaming = this.state.renamingId === track.id;
    const isHovered = this.state.hoveredRowId === track.id;
    const isMenuOpenForRow = this.state.rowMenuOpenFor === track.id;
    const rowActive = isCurrent || isMenuOpenForRow;
    // Reveal-on-hover controls stay mounted at zero opacity so the row's
    // column widths never shift under the pointer.
    const revealed = isHovered || isMenuOpenForRow;
    const isDragging = this.state.draggingId === track.id;
    const isDropTarget = this.state.dropTargetId === track.id && this.state.draggingId !== track.id;
    // Draw the landing line as an inset shadow rather than an extra
    // element, so showing it can't nudge the list's layout mid-drag.
    const dropLine = isDropTarget
      ? `inset 0 ${this.state.dropBelow ? '-2px' : '2px'} 0 0 rgba(232, 195, 110, 0.95)`
      : null;

    return (
      <div
        key={track.id}
        ref={(el) => { if (el) this.rowRefs.set(track.id, el); else this.rowRefs.delete(track.id); }}
        className="vh-music-row flex items-center"
        style={{
          gap: '0.4cqw',
          padding: '0.34cqw 0.4cqw',
          borderRadius: ROW_RADIUS,
          background: rowActive ? `${GOLD} 0.11)` : (isHovered ? `${GOLD} 0.055)` : 'transparent'),
          boxShadow: dropLine || 'none',
          opacity: isDragging ? 0.35 : 1,
          transition: 'background 140ms ease, opacity 140ms ease, box-shadow 100ms ease',
          animationDelay: `${Math.min(idx, 9) * 26}ms`,
        }}
        onDragOver={(e) => this.handleRowDragOver(e, track)}
        onDrop={(e) => this.handleDropOnRow(e, track)}
        onMouseEnter={() => this.setState({ hoveredRowId: track.id })}
        onMouseLeave={() => this.setState((s) => (s.hoveredRowId === track.id ? { hoveredRowId: null } : null))}
      >
        <span
          aria-hidden
          className="vh-music-grip shrink-0"
          style={{
            color: TEXT_MUTED,
            lineHeight: 0,
            opacity: revealed ? 0.55 : 0,
            pointerEvents: revealed ? 'auto' : 'none',
            transition: 'opacity 140ms ease',
          }}
          draggable={!isRenaming}
          onDragStart={(e) => this.handleDragStart(e, track)}
          onDragEnd={this.handleDragEnd}
        >
          <GripIcon />
        </span>

        <TrackIndexSlot
          index={idx + 1}
          isPlaying={isPlaying}
          isCurrent={isCurrent}
          showPlayGlyph={revealed}
          onClick={(e) => { e.stopPropagation(); playUI(UI.CLICK); this.handleRowActivate(track); }}
          label={isPlaying ? t('Pause') : t('Play')}
        />

        <div
          className="flex-1 min-w-0"
          onClick={(e) => {
            if (e.target.closest('input')) return;
            playUI(UI.CLICK);
            this.handleRowActivate(track);
          }}
          onKeyDown={(e) => this.handleRowKeyDown(e, track)}
          tabIndex={0}
          role="button"
        >
          {isRenaming ? (
            <input
              type="text"
              autoFocus
              value={this.state.renameDraft}
              onInput={(e) => this.setState({ renameDraft: e.currentTarget.value })}
              onBlur={this.commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); this.commitRename(); }
                else if (e.key === 'Escape') { e.preventDefault(); this.cancelRename(); }
                e.stopPropagation();
              }}
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%', padding: '0.14cqw 0.3cqw', fontSize: '0.72cqw', borderRadius: 'var(--radius-sm)', background: 'rgba(0,0,0,0.35)', border: `1px solid ${GOLD} 0.45)`, color: TEXT_PRIMARY, outline: 'none' }}
            />
          ) : (
            <div className="truncate" style={{ fontSize: '0.75cqw', color: isCurrent ? TEXT_PRIMARY : TEXT_BODY, fontWeight: 500, letterSpacing: '0.01em', transition: 'color 160ms ease' }}>
              {track.displayName}
            </div>
          )}
        </div>

        {track.loop ? (
          <Tooltip content={t('Repeats on its own')} side="bottom" delay={350}>
            <span className="shrink-0" role="img" aria-label={t('Loops')} style={{ color: ACCENT_GOLD, lineHeight: 0, opacity: 0.8 }}>
              <LoopGlyph size="0.62cqw" />
            </span>
          </Tooltip>
        ) : null}

        <span
          className="tabular-nums shrink-0 text-right"
          style={{ fontSize: '0.62cqw', color: TEXT_MUTED, minWidth: '1.8cqw', letterSpacing: '0.02em' }}
        >
          {formatDuration(track.durationSeconds)}
        </span>

        <GhostIconButton
          size="1.4cqw"
          active={isMenuOpenForRow}
          style={{ opacity: revealed ? 1 : 0, pointerEvents: revealed ? 'auto' : 'none' }}
          onClick={(e) => {
            e.stopPropagation();
            playUI(UI.CLICK);
            if (this.state.rowMenuOpenFor === track.id && this.state.rowMenuVariant === 'menu') {
              this.closeRowMenu();
            } else {
              this.openRowMenu(track, 'menu', e.currentTarget);
            }
          }}
          aria-label="Track options"
        >
          <DotsIcon />
        </GhostIconButton>
      </div>
    );
  }

  renderRowMenuOverlay() {
    const trackId = this.state.rowMenuOpenFor;
    const variant = this.state.rowMenuVariant;
    const anchor = this.state.rowMenuAnchor;
    if (!trackId || !anchor) return null;
    const tracks = this.state.customMusic.library.tracks;
    const track = tracks.find((t) => t.id === trackId);
    if (!track) return null;

    if (variant === 'gain') {
      const gainPct = Math.round(track.gain * 100);
      // Slider goes 0–150% so the visual progress fill scales relative to 150 not 100.
      const fillPct = Math.min(100, (gainPct / 150) * 100);
      return (
        <RowMenuPortal key="row-menu" anchor={anchor} onClose={this.closeRowMenu}>
          <div style={{ width: '12cqw' }}>
            <SectionLabel>{t('Track gain')}</SectionLabel>
            <div className="flex items-center justify-between" style={{ margin: '0.3cqw 0 0.2cqw' }}>
              <span className="tabular-nums" style={{ fontSize: '0.6cqw', color: ACCENT_GOLD }}>{gainPct}%</span>
              <span style={{ fontSize: '0.55cqw', color: TEXT_MUTED }}>{t('max 150%')}</span>
            </div>
            <input
              type="range"
              min="0"
              max="150"
              value={gainPct}
              className="vh-slider"
              style={{ '--vh-slider-progress': `${fillPct}%` }}
 onInput={(e) => cmSetTrackGain(track.id, parseInt(e.currentTarget.value, 10) / 100)}
 />
 </div>
 </RowMenuPortal>
 );
 }

 return (
 <RowMenuPortal key="row-menu" anchor={anchor} onClose={this.closeRowMenu}>
 <MenuItem onClick={() => this.startRename(track)} label={t('Rename')} />
 <MenuItem
 onClick={() => { cmSetTrackLoop(track.id, !track.loop); this.closeRowMenu(); }}
 label={track.loop ? t('Don’t loop track') : t('Loop track')}
 accessory={track.loop ? <CheckIcon /> : null}
 />
 <MenuItem
 onClick={() => {
 const trigger = this.rowRefs.get(track.id)?.querySelector('[aria-label="Track options"]');
 this.openRowMenu(track, 'gain', trigger);
 }}
 label={t('Adjust gain')}
 />
 {this.state.customMusic.currentTrackId === track.id ? (
 <MenuItem onClick={() => { playUI(UI.CLICK); cmStop(); this.closeRowMenu(); }} label={t('Stop playback')} />
 ) : null}
 </RowMenuPortal>
 );
 }

 renderFolderMenu() {
 const anchor = this.state.folderMenuAnchor;
 if (!anchor) return null;
 const cm = this.state.customMusic;
 const folderPath = cm.library.folderPath;
 const folderMissing = cm.library.folderMissing;
 const trackCount = cm.library.tracks.length;
 return (
 <RowMenuPortal key="folder-menu" anchor={anchor} onClose={this.closeFolderMenu} minWidth="14cqw">
 <div
 style={{
 padding: '0.25cqw 0.55cqw 0.4cqw',
 maxWidth: '20cqw',
 }}
 >
 <div
 style={{
 fontSize: '0.55cqw',
 color: folderMissing ? 'rgba(220, 130, 130, 0.9)' : TEXT_MUTED,
 letterSpacing: '0.04em',
 }}
 >
 {folderMissing ? t('Folder unavailable') : t('Watching')}
 </div>
 <div
 className="truncate"
 title={folderPath || ''}
 style={{
 marginTop: '0.1cqw',
 fontSize: '0.65cqw',
 color: folderMissing ? 'rgba(220, 130, 130, 0.95)' : TEXT_BODY,
 }}
 >
                  {folderPath || EM_DASH}
 </div>
 {trackCount > 0 ? (
 <div className="tabular-nums" style={{ marginTop: '0.15cqw', fontSize: '0.55cqw', color: TEXT_MUTED }}>
 {trackCount === 1 ? t('1 track · {size}', { size: formatBytes(cm.library.totalBytes) }) : t('{n} tracks · {size}', { n: trackCount, size: formatBytes(cm.library.totalBytes) })}
 </div>
 ) : null}
 </div>
 <div style={{ borderTop: `1px solid ${GOLD} 0.12)`, margin: '0 0.25cqw 0.25cqw' }} />
 <MenuItem onClick={this.handlePickFolder} label={t('Change folder')} />
 <MenuItem onClick={this.handleClearFolder} label={t('Clear folder')} danger />
 </RowMenuPortal>
 );
 }
}

/** 'off' | 'all' | 'one'. See AudioMixerPopover.cycleRepeat. */
function repeatMode(cm, currentTrack) {
 if (currentTrack?.loop) return 'one';
 if (cm.library.libraryLoop) return 'all';
 return 'off';
}

/* ── Small building-block components ────────────────────── */

function SectionLabel({ children }) {
 return (
 <div
 style={{
 ...SECTION_HEADER_STYLE,
 fontSize: '0.58cqw',
 fontWeight: 700,
 }}
 >
 {children}
 </div>
 );
}

/**
 * The row's leading slot. At rest it is the track number; on row hover it
 * crossfades to a play/pause glyph; while playing it becomes a small
 * equalizer. One slot, three states. Instead of a permanent transport
 * button on every row.
 */
function TrackIndexSlot({ index, isPlaying, isCurrent, showPlayGlyph, onClick, label }) {
 return (
 <button
 type="button"
 className="vh-music-btn relative flex items-center justify-center shrink-0 active:scale-[0.9]"
 style={{
 width: '1.4cqw',
 height: '1.4cqw',
 borderRadius: 'var(--radius-sm)',
 color: isCurrent ? ACCENT_GOLD : TEXT_BODY,
 transition: 'color 160ms ease, transform 140ms cubic-bezier(0.23,1,0.32,1)',
 }}
 onClick={onClick}
 aria-label={label}
 >
 <SlotLayer visible={!showPlayGlyph && !isPlaying}>
 <span className="tabular-nums" style={{ fontSize: '0.62cqw', color: isCurrent ? ACCENT_GOLD : TEXT_MUTED }}>
 {index}
 </span>
 </SlotLayer>
 <SlotLayer visible={isPlaying && !showPlayGlyph}>
 <EqualizerBars />
 </SlotLayer>
 <SlotLayer visible={showPlayGlyph}>
 {isPlaying ? <PauseIcon small /> : <PlayIcon small />}
 </SlotLayer>
 </button>
 );
}

/**
 * One state of the leading slot. Each layer owns its own opacity, so
 * number → equalizer → play glyph are real crossfades; sharing a layer
 * would swap the glyph instantly under a steady opacity and read as a pop.
 */
function SlotLayer({ visible, children }) {
 return (
 <span
 aria-hidden={!visible}
 className="absolute flex items-center justify-center"
 style={{
 lineHeight: 0,
 opacity: visible ? 1 : 0,
 transform: visible ? 'scale(1)' : 'scale(0.86)',
 transition: 'opacity 130ms ease-out, transform 130ms cubic-bezier(0.23,1,0.32,1)',
 }}
 >
 {children}
 </span>
 );
}

function EqualizerBars() {
 return (
 <span className="flex items-end" style={{ gap: '0.08cqw', height: '0.7cqw' }} aria-hidden>
 {[0, 1, 2].map((i) => (
 <span
 key={i}
 className="vh-eq-bar"
 style={{
 width: '0.13cqw',
 height: '100%',
 background: 'currentColor',
 borderRadius: 'var(--radius-xs)',
 animationDelay: `${i * 160}ms`,
            animationDuration: `${760 + i * 120}ms`,
          }}
        />
      ))}
    </span>
  );
}

/**
 * Slim playback scrubber for the currently-loaded custom track.
 * Shows a play/pause button, elapsed / total time and a draggable
 * progress bar so the player can seek anywhere in the file, like
 * Spotify or Apple Music.
 */
function PlaybackProgress({ currentTimeSec, durationSec, isPlaying, hasTrack, repeat, onSeek, onTogglePlay, onCycleRepeat }) {
  const known = hasTrack && Number.isFinite(durationSec) && durationSec > 0;
  const disabled = !hasTrack;
  const progressPct = known ? Math.min(100, (currentTimeSec / durationSec) * 100) : 0;
  const repeatState = repeat === 'one' ? t('this track') : repeat === 'all' ? t('whole library') : t('off');
  const repeatNext = repeat === 'one' ? t('off') : repeat === 'all' ? (hasTrack ? t('this track') : t('off')) : t('whole library');
  return (
    <div
      className="flex items-center"
      style={{
        marginTop: '0.8cqw',
        padding: `0 ${LIST_PADDING}`,
        gap: '0.7cqw',
        opacity: disabled ? 0.5 : 1,
        transition: 'opacity 140ms ease',
      }}
    >
      <GhostIconButton
        active={isPlaying}
        size="2.2cqw"
        onClick={onTogglePlay}
        disabled={disabled}
        aria-label={isPlaying ? t('Pause') : t('Play')}
      >
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </GhostIconButton>
      <span className="tabular-nums shrink-0" style={{ fontSize: '0.68cqw', fontWeight: 600, color: known ? ACCENT_GOLD : TEXT_MUTED, minWidth: '2.6cqw', letterSpacing: '0.02em' }}>
        {hasTrack ? formatDuration(currentTimeSec) : '0:00'}
      </span>
      <input
        type="range"
        min="0"
        max={known ? Math.floor(durationSec) : 100}
        step="1"
        value={hasTrack ? Math.floor(currentTimeSec) : 0}
        className="vh-scrubber flex-1"
        style={{ '--vh-slider-progress': `${progressPct}%` }}
        disabled={!known}
        onInput={(e) => onSeek(parseInt(e.currentTarget.value, 10))}
      />
      <span className="tabular-nums shrink-0 text-right" style={{ fontSize: '0.68cqw', fontWeight: 600, color: TEXT_MUTED, minWidth: '2.6cqw', letterSpacing: '0.02em' }}>
        {known ? formatDuration(durationSec) : '--:--'}
      </span>
      <Tooltip content={t('Repeat: {state}. Click for {next}', { state: repeatState, next: repeatNext })} side="bottom">
        <GhostIconButton
          active={repeat !== 'off'}
          onClick={onCycleRepeat}
          aria-label={t('Repeat {state}', { state: repeatState })}
        >
          <span className="relative flex items-center justify-center" style={{ lineHeight: 0 }}>
            <LoopGlyph />
            {repeat === 'one' ? (
              <span
                className="absolute tabular-nums"
                style={{ fontSize: '0.38cqw', fontWeight: 800, lineHeight: 1, transform: 'translateY(0.02cqw)' }}
              >
                1
              </span>
            ) : null}
          </span>
        </GhostIconButton>
      </Tooltip>
    </div>
  );
}

/**
 * One-line volume control: speaker (mute toggle when `onToggleMute` is
 * given), slider, value. Sits directly under the section it belongs to,
 * so the label is carried by position rather than by another word.
 */
function VolumeRow({ value, onChange, muted = false, onToggleMute }) {
  const pct = Math.round(value * 100);
  const interactive = typeof onToggleMute === 'function';
  const iconColor = muted ? 'rgba(220, 130, 130, 0.9)' : ACCENT_GOLD;
  const icon = muted ? <SpeakerMutedIcon /> : <SpeakerIcon />;
  return (
    <div className="flex items-center" style={{ marginTop: '0.75cqw', padding: `0 ${LIST_PADDING}`, gap: '0.5cqw' }}>
      {interactive ? (
        <Tooltip content={muted ? t('Music is muted. Click to enable') : t('Mute music')} side="bottom">
          <button
            type="button"
            onClick={onToggleMute}
            aria-label={muted ? t('Enable music') : t('Mute music')}
            className="vh-music-btn flex items-center justify-center shrink-0 hover:scale-[1.04] active:scale-[0.92]"
            style={{
              width: '1.35cqw',
              height: '1.35cqw',
              borderRadius: CTRL_RADIUS,
              color: iconColor,
              background: 'transparent',
              border: 'none',
              transition: 'background 160ms ease, color 160ms ease, transform 140ms cubic-bezier(0.23,1,0.32,1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = muted ? 'rgba(220, 100, 100, 0.14)' : `${GOLD} 0.12)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {icon}
          </button>
        </Tooltip>
      ) : (
        <span
          aria-hidden
          className="flex items-center justify-center shrink-0"
          style={{ width: '1.35cqw', height: '1.35cqw', color: `${GOLD} 0.6)` }}
        >
          {icon}
        </span>
      )}
      <input
        type="range"
        min="0"
        max="100"
        value={pct}
        className="vh-slider flex-1"
        style={{ '--vh-slider-progress': `${pct}%` }}
        disabled={muted}
        onInput={(e) => onChange(parseInt(e.currentTarget.value, 10) / 100)}
      />
      <span className="tabular-nums text-right shrink-0" style={{ fontSize: '0.6cqw', color: muted ? TEXT_MUTED : ACCENT_GOLD, fontWeight: 600, minWidth: '1.5cqw' }}>
        {muted ? t('Off') : pct}
      </span>
    </div>
  );
}

/**
 * Ghost icon button. No chrome at rest. The glyph carries the meaning and
 * a gold wash only appears on hover or while the control is engaged, so a
 * row of transport controls reads as a row of icons rather than a row of
 * carved plates competing with the track list.
 *
 *  Wrapping `Tooltip` injects its own mouse handlers via cloneElement,
 *  so we explicitly extract `onMouseEnter` / `onMouseLeave` from props
 *  and chain them after our visual hover styling. Otherwise the
 *  spread `{...rest}` would overwrite our handlers and the hover
 *  colour change would silently disappear.
 */
function GhostIconButton({
  active = false,
  size = '1.7cqw',
  onClick,
  disabled,
  children,
  style,
  onMouseEnter,
  onMouseLeave,
  ...rest
}) {
  const restBg = active ? `${GOLD} 0.14)` : 'transparent';
  return (
    <button
      type="button"
      className="vh-music-btn flex items-center justify-center shrink-0 hover:scale-[1.04] active:scale-[0.94]"
      style={{
        width: size,
        height: size,
        borderRadius: CTRL_RADIUS,
        background: restBg,
        border: 'none',
        color: active ? ACCENT_GOLD : TEXT_BODY,
        opacity: disabled ? 0.45 : 1,
        transition: 'transform 140ms cubic-bezier(0.23,1,0.32,1), background 160ms ease, color 160ms ease, opacity 140ms ease',
        ...style,
      }}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = active ? `${GOLD} 0.22)` : `${GOLD} 0.12)`;
          e.currentTarget.style.color = ACCENT_GOLD;
        }
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = restBg;
          e.currentTarget.style.color = active ? ACCENT_GOLD : TEXT_BODY;
        }
        onMouseLeave?.(e);
      }}
      {...rest}
    >
      {children}
    </button>
  );
}


function ChevronIcon({ dir }) {
  return (
    <svg style={{ width: '0.6cqw', height: '0.6cqw' }} viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {dir === 'prev' ? <path d="M6.5 1.5 L2.5 5 L6.5 8.5" /> : <path d="M3.5 1.5 L7.5 5 L3.5 8.5" />}
    </svg>
  );
}

function FolderIcon({ size = '0.85cqw' }) {
  return (
    <svg viewBox="0 0 16 16" style={{ width: size, height: size }} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 5 a1 1 0 0 1 1-1 h3.5 l1.5 1.5 H13 a1 1 0 0 1 1 1 V12 a1 1 0 0 1 -1 1 H3 a1 1 0 0 1 -1 -1 Z" />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg viewBox="0 0 16 16" style={{ width: '0.65cqw', height: '0.65cqw' }} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 6 H5 L9 3 V13 L5 10 H3 Z" fill="currentColor" />
      <path d="M11.5 5.5 a3.5 3.5 0 0 1 0 5" />
    </svg>
  );
}

function SpeakerMutedIcon() {
  return (
    <svg viewBox="0 0 16 16" style={{ width: '0.65cqw', height: '0.65cqw' }} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 6 H5 L9 3 V13 L5 10 H3 Z" fill="currentColor" />
      <path d="M11 6 L14 9 M14 6 L11 9" />
    </svg>
  );
}

function MenuItem({ onClick, label, danger = false, accessory = null }) {
  return (
    <button
      type="button"
      className="w-full text-left flex items-center justify-between"
      style={{
        padding: '0.4cqw 0.55cqw',
        borderRadius: MENU_ITEM_RADIUS,
        fontSize: '0.7cqw',
        color: danger ? 'rgba(220, 130, 130, 0.95)' : TEXT_BODY,
        background: 'transparent',
        letterSpacing: '0.01em',
        transition: 'background 140ms ease, color 140ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = danger ? 'rgba(220, 100, 100, 0.1)' : `${GOLD} 0.12)`;
        e.currentTarget.style.color = danger ? '#e87878' : TEXT_PRIMARY;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = danger ? 'rgba(220, 130, 130, 0.95)' : TEXT_BODY;
      }}
      onClick={(e) => { e.stopPropagation(); onClick(e); }}
    >
      <span>{label}</span>
      {accessory}
    </button>
  );
}

function RowMenuPortal({ anchor, onClose, children, minWidth = '11cqw' }) {
  const reduced = prefersReducedMotion();
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, zIndex: 19 }} onClick={onClose} />
      <motion.div
        initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: -4 }}
        animate={reduced ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0, transition: { duration: 0.16, ease: [0.23, 1, 0.32, 1] } }}
        exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: -3, transition: { duration: 0.1, ease: [0.4, 0, 1, 1] } }}
        style={{
          ...OPAQUE_TEXTURED_PANEL_STYLE,
          position: 'absolute',
          top: anchor.top,
          ...(anchor.right !== undefined ? { right: anchor.right } : { left: anchor.left }),
          zIndex: 20,
          minWidth,
          padding: MENU_PADDING,
          borderRadius: MENU_RADIUS,
          transformOrigin: anchor.right !== undefined ? 'top right' : 'top left',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <FourCorners color={`${GOLD} 0.5)`} size="1.1cqw" />
        {children}
      </motion.div>
    </>
  );
}

/* ── Icons & visualizers ────────────────────────────────── */

function GripIcon() {
  return (
    <svg viewBox="0 0 8 12" fill="currentColor" aria-hidden style={{ display: 'block', width: '0.45cqw', height: '0.72cqw' }}>
      <circle cx="2" cy="2" r="0.9" /><circle cx="6" cy="2" r="0.9" />
      <circle cx="2" cy="6" r="0.9" /><circle cx="6" cy="6" r="0.9" />
      <circle cx="2" cy="10" r="0.9" /><circle cx="6" cy="10" r="0.9" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg viewBox="0 0 16 16" style={{ width: '0.75cqw', height: '0.75cqw' }} fill="currentColor" aria-hidden>
      <circle cx="3" cy="8" r="1.6" /><circle cx="8" cy="8" r="1.6" /><circle cx="13" cy="8" r="1.6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" style={{ width: '0.65cqw', height: '0.65cqw', color: ACCENT_GOLD }} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 8 L7 12 L13 4" />
    </svg>
  );
}

function LoopGlyph({ size = '0.75cqw' }) {
  return (
    <svg viewBox="0 0 14 14" style={{ width: size, height: size }} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 6 V5 a2 2 0 0 1 2-2 h7 M12 8 v1 a2 2 0 0 1 -2 2 H3" />
      <path d="M10 1 l2 2 l-2 2 M4 9 l-2 2 l2 2" />
    </svg>
  );
}

function PlayIcon({ small = false }) {
  const s = small ? '0.6cqw' : '0.75cqw';
  return <svg viewBox="0 0 10 10" style={{ width: s, height: s }} fill="currentColor"><path d="M2.5 1.5 L8.5 5 L2.5 8.5 Z" /></svg>;
}

function PauseIcon({ small = false }) {
  const s = small ? '0.6cqw' : '0.75cqw';
  return <svg viewBox="0 0 10 10" style={{ width: s, height: s }} fill="currentColor"><rect x="1.5" y="1.5" width="2.5" height="7" rx="0.5" /><rect x="6" y="1.5" width="2.5" height="7" rx="0.5" /></svg>;
}
